/* eslint-disable react/jsx-one-expression-per-line */
import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Typography } from '@material-ui/core';
import classNames from 'classnames';
import LearningCheckQuestions from '../../../micro/learningCheck/LearningCheckQuestions';
import { borderGrey, purple, shadow, universalStyles } from '../../../../app/theme';
import { IMAGE_PATHS, BASE_URL } from '../../../../constants/paths.constants';

const useStyles = makeStyles(() => ({
  ...universalStyles,
  headerImage: {
    display: 'block',
    width: '100%',
  },
  heading: {
    marginBottom: 50,
  },
  card: {
    display: 'flex',
    boxSizing: 'border-box',
    flexDirection: 'row',
    padding: '19px 71px 29px 21px',
    width: '100%',
    border: `1px solid ${borderGrey}`,
    boxShadow: `3px 3px 4px ${shadow}`,
    marginBottom: 27,
  },
  icon: {
    width: 74,
    height: 74,
  },
  sectionHeader: {
    marginBottom: 50,
  },
  cardTitle: {
    marginBottom: 7,
    paddingBottom: 7,
    fontSize: 22,
    borderBottom: `1px solid ${purple}`,
  },
  cardContent: {
    marginLeft: 17,
  },
  columnWrapper: {
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
  },
  rightColumn: {
    display: 'flex',
    flexDirection: 'column',
  },
  row: {
    marginLeft: 80,
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    marginBottom: 40,
  },
  text: {
    margin: '0 0 12px 0',
  },
  columnImage: {
    marginLeft: 50,
    paddingBottom: 50,
    maxHeight: 1120,
  },
  imageHeader: {
    marginBottom: 8,
    fontSize: 22,
    '& > em': {
      display: 'block',
    },
  },
  imageList: {
    paddingLeft: 30,
    '& > li': {
      marginTop: 8,
    },
  },
  imageColumnWrapper: {
    justifyContent: 'space-around',
  },
  column: {
    boxSizing: 'border-box',
    width: '40%',
  },
  bottomImage: {
    width: '100%',
  },
  caption: {
    marginBottom: 15,
    textAlign: 'center',
    marginTop: 50,
    fontSize: 18,
    fontWeight: 'bold',
  },
}));

type AcquisitionPage07Props = {
  setNextDisabled: (disabled: boolean) => void;
};

const AcquisitionPage07 = ({ setNextDisabled }: AcquisitionPage07Props): JSX.Element => {
  const classes = useStyles();

  return (
    <>
      <div className={classes.grayBox}>
        <img
          className={classes.headerImage}
          src={`${BASE_URL}${IMAGE_PATHS.STEPS[1].ACQUISITION_STEP_3.path}`}
          alt={`${IMAGE_PATHS.STEPS[1].ACQUISITION_STEP_3.alt}`}
        />
        <div className={classes.whiteBox}>
          <Typography variant="h2">STEP 3:</Typography>
          <Typography variant="h3" className={classes.sectionHeader}>
            Prepare the Axumin Dose and Administer
          </Typography>
          <div className={classes.columnWrapper}>
            <img
              className={classes.columnImage}
              src={`${BASE_URL}${IMAGE_PATHS.PREPARE_DOSE_TIMELINE.path}`}
              alt={IMAGE_PATHS.PREPARE_DOSE_TIMELINE.alt}
            />
            <div className={classes.rightColumn}>
              <div className={classes.row}>
                <Typography variant="h4" className={classes.imageHeader}>
                  Dilute Axumin (if needed)
                </Typography>
                <Typography variant="body1" className={classes.text}>
                  Axumin dose may be diluted with sterile sodium chloride injection, 0.9%
                </Typography>
                <Typography variant="body1" className={classes.text}>
                  Do not administer a volume of undiluted Axumin that is greater than 5 mL
                </Typography>
                <ul className={classNames(classes.list, classes.imageList)}>
                  <li>
                    This volume keeps administration of all species and solvents within limits generally recognized as
                    safe
                  </li>
                  <li>
                    After Axumin injection, administer an intravenous flush of sterile sodium chloride injection, 0.9%,
                    to ensure full delivery of dose
                  </li>
                </ul>
              </div>
              <div className={classes.row}>
                <Typography variant="h4" className={classes.imageHeader}>
                  Prepare administration
                </Typography>
                <Typography variant="body1" className={classes.text}>
                  Recommended dose is 370 MBq (10 mCi)
                </Typography>
                <Typography variant="body1" className={classes.text}>
                  Do not use Axumin more than 10 hours after end of synthesis and dispose of in accordance with
                  institutional guidelines
                </Typography>
                <ul className={classNames(classes.list, classes.imageList)}>
                  <li>
                    Adult effective dose resulting from administration of recommended activity of 370 MBq of Axumin is
                    8.2 mSv
                  </li>
                  <li>
                    For administered activity of 370 MBq, typical radiation doses to critical organs, pancreas, cardiac
                    wall and uterine wall are 37.8 mGy, 19.1 mGy and 16.5 mGy, respectively
                  </li>
                </ul>
              </div>
              <div className={classes.row}>
                <Typography variant="h4" className={classes.imageHeader}>
                  Position patient in PET/CT or PET/MRI scan table
                </Typography>
                <Typography variant="body1" className={classes.text}>
                  Administer dose as an intravenous bolus injection while patient is positioned on PET/CT or PET/MRI
                  scan table, with their arms adjacent to the torso
                </Typography>
              </div>
              <div className={classes.row}>
                <Typography variant="h4" className={classes.imageHeader}>
                  Administer into RIGHT arm <em>(this is preferred)</em>
                </Typography>
                <Typography variant="body1" className={classes.text}>
                  If right arm cannot be used, left arm may be used; interpretation error possible
                </Typography>
              </div>
            </div>
          </div>
          <Typography variant="h4" className={classes.imageHeader}>
            Injection into right arm is preferred
          </Typography>
          <ul className={classNames(classes.list, classes.imageList)}>
            <li>Stasis in left axillary vein may be misinterpreted as metastatic lymph node (Virchow’s node)</li>
            <li>If right arm cannot be used, beware of possibility of image interpretation error</li>
          </ul>
          <div className={classNames(classes.imageColumnWrapper, classes.columnWrapper)}>
            <div className={classes.column}>
              <Typography className={classes.caption}>
                Stasis (or venous trapping) in axillary vein <br /> (SUV<sub>max</sub> = 201)
              </Typography>
            </div>
            <div className={classes.column}>
              <Typography className={classes.caption}>Virchow’s node</Typography>
            </div>
          </div>
          <div className={classNames(classes.imageColumnWrapper, classes.columnWrapper)}>
            <div className={classes.column}>
              <img
                className={classes.bottomImage}
                src={`${BASE_URL}${IMAGE_PATHS.STATIS_VEIN.path}`}
                alt={IMAGE_PATHS.STATIS_VEIN.alt}
              />
            </div>
            <div className={classes.column}>
              <img
                className={classes.bottomImage}
                src={`${BASE_URL}${IMAGE_PATHS.VIRCHOWS_NODE.path}`}
                alt={IMAGE_PATHS.VIRCHOWS_NODE.alt}
              />
            </div>
          </div>
        </div>
      </div>
      <LearningCheckQuestions module="axuminAcquisition" page={7} setNextDisabled={setNextDisabled} trainingId={1} />
    </>
  );
};

export default AcquisitionPage07;
